import React, { useEffect, useRef } from 'react';

import edinburgh from '_assets/images/gateway/logos/edinburgh-airport.png';
import dublin from '_assets/images/gateway/logos/dublin-airport.svg';
import belfast from '_assets/images/gateway/logos/belfast-international-airport.png';
import shannon from '_assets/images/gateway/logos/shannon-airport.png';
import ocsGroup from '_assets/images/gateway/logos/ocs-group.png';
import sgsAviation from '_assets/images/gateway/logos/sgs-aviation-services.jpg';
import sydney from '_assets/images/gateway/logos/sydney-airport.png';
import aerLingus from '_assets/images/gateway/logos/aer-lingus.png';
import bristol from '_assets/images/gateway/logos/bristol-airport.png';
import inverness from '_assets/images/gateway/logos/inverness-airport.png';
import kingAbdulaziz from '_assets/images/gateway/logos/king-abdulaziz-international-airport.png';
import kingKhaled from '_assets/images/gateway/logos/king-khaled-international-airport.png';
import puntaCana from '_assets/images/gateway/logos/punta-cana-international-airport.png';
import healthcareAbroad from '_assets/images/gateway/logos/healthcare-abroad.png';

const LOGOS = [
  { src: edinburgh, alt: 'Edinburgh Airport' },
  { src: dublin, alt: 'Dublin Airport' },
  { src: belfast, alt: 'Belfast International Airport' },
  { src: shannon, alt: 'Shannon Airport' },
  { src: ocsGroup, alt: 'OCS Group' },
  { src: sgsAviation, alt: 'SGS Aviation Services' },
  { src: sydney, alt: 'Sydney Airport' },
  { src: aerLingus, alt: 'Aer Lingus' },
  { src: bristol, alt: 'Bristol Airport' },
  { src: inverness, alt: 'Inverness Airport' },
  { src: kingAbdulaziz, alt: 'King Abdulaziz International Airport' },
  { src: kingKhaled, alt: 'King Khaled International Airport' },
  { src: puntaCana, alt: 'Punta Cana International Airport' },
  { src: healthcareAbroad, alt: 'Healthcare Abroad' },
];

const iconBtnStyle = {
  width: 52,
  height: 52,
  display: 'grid',
  placeItems: 'center',
  borderRadius: 14,
  border: '1px solid rgba(32,138,239,0.35)',
  background: '#fff',
  color: '#1670c8',
  cursor: 'pointer',
  fontSize: 20,
  lineHeight: 1,
};

const LogoCarousel = () => {
  const trackRef = useRef(null);
  const stateRef = useRef({ raf: null, cloned: false, loopWidth: 0, autoPos: null, autoPaused: false, resumeAt: 0 });
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  useEffect(() => {
    if (reducedMotion) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    const state = stateRef.current;
    let last = 0;

    const step = (t) => {
      if (!state.cloned && el.children.length) {
        const originals = Array.from(el.children);
        state.loopWidth = el.scrollWidth;
        originals.forEach((node) => {
          const copy = node.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('[id]').forEach((n) => { n.id = `${n.id}-clone`; });
          el.appendChild(copy);
        });
        state.cloned = true;
        el.scrollLeft = state.loopWidth;
        state.autoPos = state.loopWidth;
      }
      const dt = last ? Math.min(t - last, 48) : 16;
      last = t;
      const held = state.autoPaused || (state.resumeAt && t < state.resumeAt);
      const loop = state.loopWidth || el.scrollWidth / 2;
      if (held) {
        state.autoPos = el.scrollLeft;
      } else if (loop > 4) {
        if (state.autoPos == null) state.autoPos = el.scrollLeft || loop;
        state.autoPos -= dt * 0.09;
        if (state.autoPos <= 0) state.autoPos += loop;
        el.scrollLeft = Math.round(state.autoPos);
      }
      state.raf = requestAnimationFrame(step);
    };
    state.raf = requestAnimationFrame(step);
    return () => { if (state.raf) cancelAnimationFrame(state.raf); };
  }, [reducedMotion]);

  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const state = stateRef.current;
    state.autoPos = null;
    state.resumeAt = performance.now() + 4000;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.7), behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  const pause = () => { stateRef.current.autoPaused = true; };
  const resume = () => { stateRef.current.autoPaused = false; stateRef.current.autoPos = null; };

  return (
    <section id="gw-customers" aria-labelledby="customers-title" style={{ maxWidth: 1240, margin: '96px auto 0', padding: '0 32px' }}>
      <div data-reveal="true" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap', marginBottom: 32 }}>
        <div>
          <p style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 16px', fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#1670c8' }}>
            <span aria-hidden="true">→</span> Customer stories
          </p>
          <h2 id="customers-title" style={{ margin: 0, maxWidth: '24em', fontSize: 'clamp(26px, 2.6vw, 40px)', lineHeight: 1.02, fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#1c2b4a' }}>
            Trusted by teams transforming accessible aviation.
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" className="gw-icon-btn" aria-label="Previous customer logos" aria-controls="gw-logo-track" onClick={() => nudge(-1)} style={iconBtnStyle}>
            <span aria-hidden="true">←</span>
          </button>
          <button type="button" className="gw-icon-btn" aria-label="Next customer logos" aria-controls="gw-logo-track" onClick={() => nudge(1)} style={iconBtnStyle}>
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>

      <ul
        id="gw-logo-track"
        ref={trackRef}
        tabIndex={0}
        role="list"
        aria-label="Customer logos, scrollable"
        style={{ listStyle: 'none', margin: 0, padding: '6px 2px 18px', display: 'flex', gap: 20, overflowX: 'auto', scrollbarWidth: 'none' }}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onFocus={pause}
        onBlur={resume}
        onPointerDown={pause}
      >
        {LOGOS.map((logo) => (
          <li key={logo.alt} style={{ flex: '0 0 auto', scrollSnapAlign: 'start' }}>
            <div style={{ width: 232, height: 122, borderRadius: 20, background: '#ffffff', border: '1px solid rgba(32,138,239,0.18)', padding: '20px 24px', boxShadow: '0 2px 6px rgba(28,43,74,0.05)' }}>
              <img src={logo.src} alt={logo.alt} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default LogoCarousel;
